import React, { useEffect, useRef, useState } from 'react';
import { userApi } from '@api/user';
import { useRedux } from 'use-redux';
import Row from 'react-bootstrap/lib/Row';
import ChangeParentEmail from './ChangeParentEmailModal';
import { Translate } from 'react-redux-i18n';
import { toast, ToastOptions } from 'react-toastify';
import styles from './styles.module.scss';

const toastOptions: ToastOptions = { position: 'top-center' };

type ConfirmParentBannerViewProps = {
  parentEmail: string;
};

const ConfirmParentBannerView = ({
  parentEmail,
}: ConfirmParentBannerViewProps) => {
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [email, setEmail] = useState(parentEmail);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetShowEditEmail = (value: boolean) => {
    if (mountedRef.current) setShowEditEmail(value);
  };

  const safeSetEmail = (value: string) => {
    if (mountedRef.current) setEmail(value);
  };

  const onSendAgain = async () => {
    try {
      await userApi.resendConfirmParentEmail();
      if (!mountedRef.current) return;
      toast.success(
        <Translate dangerousHTML value="auth.confirm.checkMail" />,
        toastOptions
      );
    } catch (error) {
      if (!mountedRef.current) return;
      toast.error(
        <Translate dangerousHTML value="auth.confirm.error" />,
        toastOptions
      );
    }
  };

  const onChangeParentEmail = async ({ email: nextEmail }) => {
    try {
      await userApi.changeConfirmParentEmail(nextEmail);
      if (!mountedRef.current) return;
      safeSetEmail(nextEmail);
      safeSetShowEditEmail(false);
      toast.success(
        <Translate dangerousHTML value="auth.confirm.checkMail" />,
        toastOptions
      );
    } catch (error) {
      if (!mountedRef.current) return;
      toast.error(
        <Translate dangerousHTML value="auth.confirm.error" />,
        toastOptions
      );
    }
  };

  return (
    <Row className={styles[`confirm-parent-banner`]}>
      <h1>
        <span>
          <Translate value="greeting.parentConfirmation.header" />
        </span>
      </h1>
      <p>
        <Translate value="greeting.parentConfirmation.msg1" /> <b>{email}</b>.{' '}
        <Translate value="greeting.parentConfirmation.msg2" />
      </p>
      <div className={styles.buttons}>
        <button
          className="btn btn-green btn-small text-uppercase"
          onClick={onSendAgain}
        >
          <Translate value="main.buttons.sendAgain" />
        </button>
        <button
          className="btn btn-green btn-small text-uppercase"
          onClick={() => safeSetShowEditEmail(true)}
        >
          <Translate value="main.buttons.changeEmail" />
        </button>

        <ChangeParentEmail
          show={showEditEmail}
          email={email}
          close={() => safeSetShowEditEmail(false)}
          onSave={onChangeParentEmail}
        />
      </div>
    </Row>
  );
};

const ConfirmParentBanner = () => {
  const [{ auth }] = useRedux();

  const user = auth && auth.user;
  const parentConfirmation = (user && user.parentConfirmation) || {};
  const { confirmed, parentEmail } = parentConfirmation;
  const parent = user && user.parent;

  if (!user || parent || confirmed || !parentEmail) return null;

  return <ConfirmParentBannerView parentEmail={parentEmail} />;
};

export default ConfirmParentBanner;
